import React, { FC, useState, useEffect, useCallback, Fragment, useMemo, useContext } from 'react';
import { useParams } from 'react-router-dom';
import find from 'lodash/find';
import forOwn from 'lodash/forOwn';
import get from 'lodash/get';
import map from 'lodash/map';
import produce from 'immer';
import reduce from 'lodash/reduce';
import styled from 'styled-components';
import sortBy from 'lodash/sortBy';

import { Table, TR, TBody, TD } from '@ad-scripts/presentation/components/common/table';

import TableList from 'src/presentation/components/pages/shared/TableList';
import { Row } from 'src/presentation/components/pages/shared/Table';
import ivrSessionContentResource from 'src/actions/ivrs/ivrSessionContentResource';
import useDocumentResource from 'src/presentation/hooks/useDocumentResource';
import { EVENT_DETAILS_FIELDS, COMMON_FIELDS, EVENT_ID_FIELD } from './ivrConstants';
import { FormEditorProps } from 'src/presentation/components/forms/Form';
import { IvrLogsContext } from './IvrLogsConfiguration';
import { H2 } from '@ad-scripts/presentation/components/common/headers';
import { transformDateToString } from './ivrLogsUtils';
import { isLoadingStatus } from 'src/presentation/hooks/useResource';
import {
  StyledChevronUpIcon,
  StyledChevronDownIcon,
  LinkToIvr,
} from 'src/presentation/components/pages/ivrLogs/ivrLogsLayout';
import TableLayout from 'src/presentation/components/layouts/table/TableLayout';

const linkToIvrRender = (row, showOnlyNodeId) => {
  return LinkToIvr({
    name: get(row, 'ivrName'),
    id: get(row, 'event.ivrId'),
    version: get(row, 'event.ivrVersion'),
    nodeId: get(row, 'event.ivrNodeId'),
    ...(showOnlyNodeId ? { nameFn: (name, version, nodeId) => (name ? nodeId : '') } : {}),
  });
};

const sessionVariableColumnConfig = () => [
  { attr: 'name', label: 'Variable Name' },
  { attr: 'value', label: 'Variable Value', customRenderer: item => JSON.stringify(item.value, null, 2) },
];

const ivrEventColumnConfig = ({ rowsState, toggleState }) => [
  {
    attr: 'collapsible',
    customColumnStyle: () => ({ width: '10px' }),
    customRenderer: row => {
      const expanded = rowsState[get(row, EVENT_ID_FIELD)];
      return (
        <span onClick={() => toggleState(get(row, EVENT_ID_FIELD))}>
          {expanded ? <StyledChevronUpIcon /> : <StyledChevronDownIcon />}
        </span>
      );
    },
  },
  {
    attr: 'event.eventTime',
    label: 'Time',
    customRenderer: item => {
      return transformDateToString(get(item, 'event.eventTime'));
    },
  },
  { attr: 'event.ivrNodeId', label: 'Node ID', customRenderer: row => linkToIvrRender(row, true) },
  { attr: 'type', label: 'Type' },
  {
    attr: 'ivrName',
    label: 'IVR',
    customRenderer: linkToIvrRender,
  },
];

const useCollapsibleRows = ({ rows, idFieldName }) => {
  const [rowsState, setRowsState] = useState({});

  useEffect(() => {
    setRowsState(
      reduce(
        rows,
        (acc, row) => {
          const id = row[idFieldName];
          acc[id] = false;
          return acc;
        },
        {}
      )
    );
  }, [idFieldName, rows, setRowsState]);

  const toggleState = useCallback(
    id => {
      setRowsState(
        produce(rowsState, draft => {
          draft[id] = !draft[id];
        })
      );
    },
    [setRowsState, rowsState]
  );

  return { rowsState, toggleState };
};

const Details = ({ row }) => {
  const type = get(row, 'type');
  const columns = get(EVENT_DETAILS_FIELDS, `[${type}].fields`) || COMMON_FIELDS;

  return (
    <DetailsRow>
      <Table data-aid="ivrEventDetailsTable">
        <TBody>
          {map(columns, (column, index) => {
            const { label, attr, customRenderer } = column;
            const valueRender = () => (customRenderer ? customRenderer(row) : get(row, attr));

            return (
              <StyledTR key={index}>
                <LabelTD>{label}</LabelTD>
                <TD>{valueRender()}</TD>
              </StyledTR>
            );
          })}
        </TBody>
      </Table>
    </DetailsRow>
  );
};

const useEventsWithIvrs = (id, ivrs) => {
  const { loadingState: eventsLoadingState, value: content } = useDocumentResource(ivrSessionContentResource, {
    fetch: true,
    params: { id },
  });

  const eventsWithIvrName = useMemo(() => {
    const res = map(content.events, ivrEvent => {
      const ivr = find(ivrs, ivr => get(ivr, 'id') === get(ivrEvent, 'event.ivrId'));
      return { ...ivrEvent, ivrName: get(ivr, 'name', get(ivr, 'description', get(ivrEvent, 'event.ivrId'))) };
    });
    return sortBy(res, 'event.eventTime');
  }, [content, ivrs]);

  const sessionVariablesDictionary = useMemo(() => {
    const variables = [] as any;
    forOwn(content.variables, (value, key) => {
      variables.push({ name: key, value: value });
    });
    return variables;
  }, [content]);

  return {
    loadingState: eventsLoadingState,
    ivrEvents: eventsWithIvrName,
    sessionVariables: sessionVariablesDictionary,
  };
};

const IvrLogsEditor: FC<FormEditorProps> = ({ item }) => {
  const { id: idFromRoute } = useParams<{ id: string }>();

  const itemId = item ? get(item, 'id') : idFromRoute;

  const { ivrs } = useContext(IvrLogsContext);
  const { loadingState, ivrEvents, sessionVariables } = useEventsWithIvrs(itemId, ivrs);

  const { rowsState, toggleState } = useCollapsibleRows({ rows: ivrEvents, idFieldName: EVENT_ID_FIELD });

  const sessionVariableColumns = sessionVariableColumnConfig();
  const ivrEventColumns = ivrEventColumnConfig({ rowsState, toggleState });

  function defaultSessionVariableRow(item, index) {
    return (
      <Fragment key={get(item, 'name')}>
        <Row
          columnConfig={sessionVariableColumns}
          item={item}
          renderRowActions={() => <></>}
          rowDataAid={`sessionVariable[${index}]`}
        />
      </Fragment>
    );
  }

  function defaultEventRow(item, index) {
    return (
      <Fragment key={get(item, EVENT_ID_FIELD)}>
        <Row columnConfig={ivrEventColumns} item={item} renderRowActions={() => <></>} rowDataAid={`rows[${index}]`} />
        {rowsState[get(item, EVENT_ID_FIELD)] && (
          <TR key={`${get(item, EVENT_ID_FIELD)}-details`} data-aid={`rows[${index}]-details`}>
            <TD colSpan={ivrEventColumns.length + 1}>
              <Details row={item} />
            </TD>
          </TR>
        )}
      </Fragment>
    );
  }

  return (
    <StyledBody>
      <PrimaryHeader>IVR Session Variables</PrimaryHeader>
      <StyledTableList
        className="sessionVariables"
        dataAid="sessionVariables"
        columnConfig={sessionVariableColumns}
        items={sessionVariables}
        pageName=""
        renderRow={defaultSessionVariableRow}
        isLoading={isLoadingStatus(loadingState)}
      />
      <PrimaryHeader>IVR Events</PrimaryHeader>
      <StyledTableList
        className="ivrEventsTable"
        dataAid="ivrEventsTable"
        columnConfig={ivrEventColumns}
        items={ivrEvents}
        pageName=""
        renderRow={defaultEventRow}
        isLoading={isLoadingStatus(loadingState)}
      />
    </StyledBody>
  );
};

const PrimaryHeader = styled(H2)`
  margin: 0px 32px 32px 32px;
`;

const StyledTableList = styled(TableList)`
  height: auto;
  margin: 32px;
  ${TableLayout} {
    border-radius: 0;
    box-shadow: none;
    margin: 0;
    max-width: 100%;

    @media (max-width: 1280px) {
      margin-right: 0;
      width: 100%;
    }

    & > div:nth-child(1) {
      display: none;
    }
  }
`;

const StyledBody = styled.div`
  padding: 0;
  max-width: 100%;
  width: 100%;
  margin-right: auto;
`;

const DetailsRow = styled.div`
  padding: 8px 8px 8px 32px;
`;

const StyledTR = styled(TR)`
  border-bottom: 2px solid ${p => p.theme.colors.green200};
`;

const LabelTD = styled(TD)`
  width: 150px;
`;

export default IvrLogsEditor;
